/**
 * VIO often returns voucher/token images as relative paths (e.g. /uploads/...).
 * Browsers resolve those against the ops app origin and the image 404s — thumbnails fall back to placeholders.
 * Resolve with REACT_APP_VIO_PUBLIC_URL, else origin of REACT_APP_VIO_EXTERNAL_API_URL (same pattern as checkin ops).
 */

function trimBase(b) {
  return (b || '').trim().replace(/\/$/, '');
}

function pickResolveBase() {
  const pub = trimBase(process.env.REACT_APP_VIO_PUBLIC_URL);
  if (pub) return pub;
  const ext = (process.env.REACT_APP_VIO_EXTERNAL_API_URL || '').trim();
  if (!ext) return '';
  try {
    return new URL(ext).origin;
  } catch {
    return '';
  }
}

/**
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function resolveVioAssetUrl(raw) {
  if (raw == null) return null;
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^data:/i.test(s)) return s;

  const base = pickResolveBase();
  if (!base) return s;

  try {
    const path = s.startsWith('/') ? s : `/${s.replace(/^\/+/, '')}`;
    return new URL(path, `${base}/`).href;
  } catch {
    const cleanPath = s.replace(/^\//, '');
    return `${base}/${cleanPath}`;
  }
}

function firstImageFromArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const e = arr[0];
  if (typeof e === 'string') return e.trim() || null;
  if (e && typeof e === 'object') {
    const u = e.imageUrl || e.url || e.src || e.path;
    if (typeof u === 'string' && u.trim()) return u.trim();
  }
  return null;
}

/**
 * Display name from VIO `voucherSchema` object (published list).
 * Prefer localized map (en, zh, ms, …), then any string value, then top-level fallbacks.
 */
export function pickVoucherSchemaDisplayName(voucherSchema) {
  const s = voucherSchema || {};
  const n = s.voucherSchemaName;
  if (typeof n === 'string' && n.trim()) return n.trim();
  if (n && typeof n === 'object') {
    const preferred = ['en', 'zh', 'ms', 'cn', 'zh-CN', 'zh-TW', 'default'];
    for (const k of preferred) {
      const v = n[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    for (const k of Object.keys(n)) {
      const v = n[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  if (typeof s.name === 'string' && s.name.trim()) return s.name.trim();
  if (typeof s.voucherName === 'string' && s.voucherName.trim()) return s.voucherName.trim();
  if (typeof s.title === 'string' && s.title.trim()) return s.title.trim();
  return 'Unnamed Voucher';
}

/** Raw image path/URL from VIO published voucher list item */
export function pickVoucherSchemaImageRaw(voucherSchema, listItem) {
  const s = voucherSchema || {};
  const item = listItem || {};
  const img = s.image;
  const candidates = [
    typeof img === 'string' ? img : null,
    img?.imageUrl,
    img?.url,
    s.imageUrl,
    item.imageUrl,
    firstImageFromArray(s.images),
    firstImageFromArray(item.images),
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim()) return String(c).trim();
  }
  return null;
}

/** Raw logo path/URL from VIO token list item (miniapp or external) */
export function pickTokenLogoRaw(item) {
  if (!item) return null;
  const candidates = [
    item.logo?.imageUrl,
    item.logo?.url,
    typeof item.logo === 'string' ? item.logo : null,
    item.icon,
    item.imageUrl,
    item.tokenImageUrl,
    firstImageFromArray(item.images),
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim()) return String(c).trim();
  }
  return null;
}
